import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import {
  addDays,
  addMonths,
  clampDay,
  dayToDate,
  endOfWeek,
  inRange,
  isDay,
  joinDateTime,
  monthGrid,
  monthTitle,
  nearestTime,
  parseDateText,
  parseTimeText,
  splitDateTime,
  spokenDay,
  startOfMonth,
  startOfWeek,
  timeOptions,
  toDateInput,
  toTimeInput,
} from '@/lib/dates';
import { formatDay } from '@/lib/format';
import { Field } from './Field';
import { Icon } from './Icon';
import { Popover, usePopover } from './Popover';

// Date and time boxes you can type into ("tomorrow", "fri", "16/10", "2pm") or pick from: a calendar for days and a
// list of times. Values are plain local strings: 'YYYY-MM-DD', 'HH:mm' and 'YYYY-MM-DDTHH:mm'.

interface BoxProps {
  id?: string;
  name?: string;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  required?: boolean;
  /** Class of the text box: "input" unless given, "cell-input" for editing in place. */
  className?: string;
  /** Class of the wrapper around the box and its button, for layout. */
  wrapClassName?: string;
  /** Offers a way to empty the box from the picker. */
  clearable?: boolean;
  /** Keys the control does not use itself. */
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  /** Called once focus leaves the whole control, its calendar or list included. */
  onBlur?: (e: FocusEvent<HTMLElement>) => void;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
  'data-autofocus'?: boolean;
}

const isTrue = (v: boolean | 'true' | 'false' | undefined) => v === true || v === 'true';

function shake(el: HTMLElement | null) {
  if (!el) return;
  el.classList.remove('shake');
  void el.offsetWidth;
  el.classList.add('shake');
  el.addEventListener('animationend', () => el.classList.remove('shake'), { once: true });
}

/** Focus moving inside the control (box, button, calendar or list) is not a blur of the control. */
function useWholeBlur(wrap: RefObject<HTMLElement | null>, popId: string, leave: (e: FocusEvent<HTMLElement>) => void) {
  return (e: FocusEvent<HTMLElement>) => {
    const next = e.relatedTarget;
    if (next instanceof Node && (wrap.current?.contains(next) || document.getElementById(popId)?.contains(next))) {
      e.stopPropagation();
      return;
    }
    leave(e);
  };
}

/** Selects the whole text when you click into the box, so typing replaces it. */
function useSelectOnFocus(input: RefObject<HTMLInputElement | null>) {
  const fresh = useRef(false);
  // React focuses an autoFocus box while it commits, without calling onFocus, so the text is selected here instead.
  useEffect(() => {
    if (input.current && document.activeElement === input.current) input.current.select();
  }, [input]);
  return {
    onFocus: () => input.current?.select(),
    onMouseDown: () => {
      fresh.current = document.activeElement !== input.current;
    },
    onMouseUp: (e: { preventDefault: () => void }) => {
      if (!fresh.current) return;
      fresh.current = false;
      e.preventDefault();
    },
  };
}

const showDay = (value: string) => (isDay(value) ? formatDay(dayToDate(value)) : value);

/* ---------- date ---------- */

export interface DateInputProps extends BoxProps {
  /** 'YYYY-MM-DD', or '' for no day. */
  value: string;
  onChange: (value: string) => void;
  /** Earliest day you can pick, 'YYYY-MM-DD'. */
  min?: string;
  /** Latest day you can pick, 'YYYY-MM-DD'. */
  max?: string;
}

/**
 * A day box that reads typing as you go and opens a calendar from its button, Alt+Down or Down.
 * The ref is the text box itself.
 */
export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(function DateInput(props, ref) {
  const {
    value,
    onChange,
    min,
    max,
    id,
    name,
    placeholder = 'Pick a date',
    autoFocus,
    disabled,
    required,
    className = 'input',
    wrapClassName,
    clearable,
    onKeyDown,
    onBlur,
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, []);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const popId = useId();
  const pop = usePopover('dialog');
  const [text, setText] = useState(() => showDay(value));
  const [bad, setBad] = useState(false);
  const [cursor, setCursor] = useState(() => toDateInput(new Date()));
  const emitted = useRef(value);
  const select = useSelectOnFocus(inputRef);

  // A new value from outside (a quick pick, a reset) replaces whatever is in the box.
  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    setText(showDay(value));
    setBad(false);
  }, [value]);

  // If the calendar closes with focus inside it (it scrolled away, say), focus goes back to the box.
  useEffect(() => {
    if (pop.open) return;
    const active = document.activeElement;
    if (active && document.getElementById(popId)?.contains(active)) inputRef.current?.focus({ preventScroll: true });
  }, [pop.open, popId]);

  const emit = (next: string) => {
    emitted.current = next;
    if (next !== value) onChange(next);
  };

  const onText = (next: string) => {
    setText(next);
    const day = parseDateText(next, { today: toDateInput(new Date()), min, max });
    if (day === null) {
      setBad(true);
      return;
    }
    setBad(false);
    emit(day);
    if (day && pop.open) setCursor(clampDay(day, min, max));
  };

  const openCalendar = () => {
    if (disabled || !wrapRef.current) return;
    setCursor(clampDay(isDay(value) ? value : toDateInput(new Date()), min, max));
    pop.openAt(wrapRef.current);
  };

  const closeCalendar = () => {
    inputRef.current?.focus({ preventScroll: true });
    pop.close();
  };

  const pick = (day: string) => {
    emit(day);
    setText(showDay(day));
    setBad(false);
    closeCalendar();
  };

  const clear = () => {
    emit('');
    setText('');
    setBad(false);
    closeCalendar();
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    const consume = () => {
      e.preventDefault();
      e.stopPropagation();
    };
    if (e.key === 'ArrowDown' && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      consume();
      if (!pop.open) openCalendar();
      else document.getElementById(popId)?.querySelector<HTMLElement>('.cal-day[tabindex="0"]')?.focus({ preventScroll: true });
      return;
    }
    if (e.key === 'Escape' && pop.open) {
      consume();
      pop.close();
      return;
    }
    if (e.key === 'Enter' && bad) {
      consume();
      shake(wrapRef.current);
      return;
    }
    if (e.key === 'Enter' && pop.open) {
      consume();
      pop.close();
      return;
    }
    onKeyDown?.(e);
  };

  const onWrapBlur = useWholeBlur(wrapRef, popId, (e) => {
    setText(showDay(value));
    setBad(false);
    if (pop.open) pop.close();
    onBlur?.(e);
  });

  const outOfRange = isDay(value) && !inRange(value, min, max);
  const invalid = bad || outOfRange || isTrue(props['aria-invalid']);

  return (
    <span ref={wrapRef} className={['dt', wrapClassName].filter(Boolean).join(' ')} onBlur={onWrapBlur}>
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="text"
        className={className}
        value={text}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        autoFocus={autoFocus}
        disabled={disabled}
        role="combobox"
        aria-haspopup="dialog"
        aria-expanded={pop.open}
        aria-controls={pop.open ? popId : undefined}
        aria-autocomplete="none"
        aria-required={required || undefined}
        aria-label={props['aria-label']}
        aria-labelledby={props['aria-labelledby']}
        aria-describedby={props['aria-describedby']}
        aria-invalid={invalid || undefined}
        data-autofocus={props['data-autofocus'] || undefined}
        onChange={(e) => onText(e.target.value)}
        onKeyDown={onKey}
        {...select}
      />
      <button
        type="button"
        className="dt-btn"
        tabIndex={-1}
        aria-label="Choose a date"
        aria-haspopup="dialog"
        aria-expanded={pop.open}
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          if (pop.open) closeCalendar();
          else openCalendar();
        }}
      >
        <Icon name="calendar" />
      </button>
      <Popover
        state={pop}
        id={popId}
        role="dialog"
        label="Choose a date"
        align="left"
        width={260}
        maxWidth={260}
        className="cal-pop"
        initialFocus='.cal-day[tabindex="0"]'
      >
        <Calendar
          value={isDay(value) ? value : ''}
          cursor={cursor}
          onCursor={setCursor}
          min={min}
          max={max}
          clearable={!!clearable && value !== ''}
          onPick={pick}
          onClear={clear}
          onClose={closeCalendar}
        />
      </Popover>
    </span>
  );
});

const WEEKDAY_HEADS: [string, string][] = [
  ['Mo', 'Monday'],
  ['Tu', 'Tuesday'],
  ['We', 'Wednesday'],
  ['Th', 'Thursday'],
  ['Fr', 'Friday'],
  ['Sa', 'Saturday'],
  ['Su', 'Sunday'],
];

interface CalendarProps {
  value: string;
  /** The day that has keyboard focus, which also sets the month shown. */
  cursor: string;
  onCursor: (day: string) => void;
  min?: string;
  max?: string;
  clearable: boolean;
  onPick: (day: string) => void;
  onClear: () => void;
  onClose: () => void;
}

function Calendar({ value, cursor, onCursor, min, max, clearable, onPick, onClear, onClose }: CalendarProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const follow = useRef(false);
  const today = toDateInput(new Date());
  const month = cursor.slice(0, 7);
  const weeks = useMemo(() => {
    const days = monthGrid(`${month}-01`);
    return Array.from({ length: 6 }, (_, i) => days.slice(i * 7, i * 7 + 7));
  }, [month]);
  const first = startOfMonth(cursor);
  const last = addDays(addMonths(first, 1), -1);

  const focusCursor = () => rootRef.current?.querySelector<HTMLElement>('.cal-day[tabindex="0"]')?.focus({ preventScroll: true });

  // Focus follows the cursor when you move it with the keys.
  useEffect(() => {
    if (!follow.current) return;
    follow.current = false;
    focusCursor();
  }, [cursor]);

  const go = (day: string, focusDay: boolean) => {
    const next = clampDay(day, min, max);
    if (next === cursor) {
      if (focusDay) focusCursor();
      return;
    }
    follow.current = focusDay;
    onCursor(next);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Everything typed here belongs to the calendar, not to the dialog, row or page behind it.
    e.stopPropagation();
    let next: string;
    switch (e.key) {
      case 'ArrowLeft':
        next = addDays(cursor, -1);
        break;
      case 'ArrowRight':
        next = addDays(cursor, 1);
        break;
      case 'ArrowUp':
        next = addDays(cursor, -7);
        break;
      case 'ArrowDown':
        next = addDays(cursor, 7);
        break;
      case 'PageUp':
        next = addMonths(cursor, e.shiftKey ? -12 : -1);
        break;
      case 'PageDown':
        next = addMonths(cursor, e.shiftKey ? 12 : 1);
        break;
      case 'Home':
        next = startOfWeek(cursor);
        break;
      case 'End':
        next = endOfWeek(cursor);
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        return;
      case 'Tab': {
        // The calendar keeps Tab to itself, like any dialog: previous month, next month, the day, then the footer.
        e.preventDefault();
        const stops = [...(rootRef.current?.querySelectorAll<HTMLElement>('button:not([disabled])') ?? [])].filter((b) => b.tabIndex !== -1);
        if (!stops.length) return;
        const i = stops.indexOf(document.activeElement as HTMLElement);
        const to = e.shiftKey ? (i <= 0 ? stops.length - 1 : i - 1) : (i + 1) % stops.length;
        stops[to]!.focus();
        return;
      }
      default:
        // Enter and Space press the focused button, which picks a day or turns the month.
        return;
    }
    e.preventDefault();
    go(next, true);
  };

  return (
    <div
      ref={rootRef}
      className="cal"
      onKeyDown={onKeyDown}
      // Clicks pick without moving focus, and stay out of the rows and dialogs behind the portal.
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="cal-head">
        <button type="button" className="cal-nav" aria-label="Previous month" disabled={!!min && first <= min} onClick={() => go(addMonths(cursor, -1), false)}>
          <Icon name="chev-left" />
        </button>
        <div className="cal-title" id={titleId} aria-live="polite">
          {monthTitle(cursor)}
        </div>
        <button type="button" className="cal-nav" aria-label="Next month" disabled={!!max && last >= max} onClick={() => go(addMonths(cursor, 1), false)}>
          <Icon name="chev-right" />
        </button>
      </div>
      <table className="cal-grid" role="grid" aria-labelledby={titleId}>
        <thead>
          <tr>
            {WEEKDAY_HEADS.map(([short, long]) => (
              <th key={short} scope="col" abbr={long}>
                {short}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week) => (
            <tr key={week[0]}>
              {week.map((day) => {
                const selected = day === value;
                const cls = ['cal-day', day.startsWith(month) ? '' : 'out', selected ? 'sel' : '', day === today ? 'today' : ''].filter(Boolean).join(' ');
                return (
                  <td key={day} aria-selected={selected || undefined}>
                    <button
                      type="button"
                      className={cls}
                      tabIndex={day === cursor ? 0 : -1}
                      aria-label={spokenDay(day)}
                      aria-current={day === today ? 'date' : undefined}
                      disabled={!inRange(day, min, max)}
                      onClick={() => onPick(day)}
                    >
                      {Number(day.slice(8))}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {inRange(today, min, max) || clearable ? (
        <div className="cal-foot">
          {inRange(today, min, max) ? (
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => onPick(today)}>
              Today
            </button>
          ) : (
            <span />
          )}
          {clearable ? (
            <button type="button" className="btn btn-sm btn-ghost" onClick={onClear}>
              Clear
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/* ---------- time ---------- */

export interface TimeInputProps extends BoxProps {
  /** 'HH:mm', or '' for no time. */
  value: string;
  onChange: (value: string) => void;
  /** Minutes between the times in the list. */
  step?: number;
}

/**
 * A time box that reads typing as you go ("930", "2pm", "noon") and offers times every `step` minutes from its
 * button, Alt+Down or Down. Focus stays in the box while you move through the list. The ref is the text box itself.
 */
export const TimeInput = forwardRef<HTMLInputElement, TimeInputProps>(function TimeInput(props, ref) {
  const {
    value,
    onChange,
    step = 15,
    id,
    name,
    placeholder = 'Time',
    autoFocus,
    disabled,
    required,
    className = 'input',
    wrapClassName,
    clearable,
    onKeyDown,
    onBlur,
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, []);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const listId = useId();
  const pop = usePopover('menu');
  const options = useMemo(() => timeOptions(step), [step]);
  const [text, setText] = useState(value);
  const [bad, setBad] = useState(false);
  const [active, setActive] = useState(-1);
  // Enter takes the highlighted time unless you typed a time of your own since the list opened.
  const [typed, setTyped] = useState(false);
  // Counts openings so the list starts afresh, centred on the chosen time, every time it opens.
  const [opened, setOpened] = useState(0);
  const emitted = useRef(value);
  const select = useSelectOnFocus(inputRef);

  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    setText(value);
    setBad(false);
  }, [value]);

  const emit = (next: string) => {
    emitted.current = next;
    if (next !== value) onChange(next);
  };

  const onText = (next: string) => {
    setText(next);
    setTyped(true);
    const time = parseTimeText(next);
    if (time === null) {
      setBad(true);
      return;
    }
    setBad(false);
    emit(time);
    if (time && pop.open) setActive(nearestTime(options, time));
  };

  const openList = () => {
    if (disabled || !wrapRef.current) return;
    const exact = options.indexOf(value);
    setActive(exact >= 0 ? exact : nearestTime(options, value || toTimeInput(new Date())));
    setTyped(value !== '' && exact === -1);
    setOpened((n) => n + 1);
    pop.openAt(wrapRef.current);
  };

  const pick = (i: number) => {
    const time = options[i];
    if (time === undefined) return;
    emit(time);
    setText(time);
    setBad(false);
    pop.close();
  };

  const clear = () => {
    emit('');
    setText('');
    setBad(false);
    pop.close();
    inputRef.current?.focus({ preventScroll: true });
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    const consume = () => {
      e.preventDefault();
      e.stopPropagation();
    };
    const page = Math.max(1, Math.round(60 / Math.max(1, step)));
    if (e.key === 'ArrowDown' && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      consume();
      if (!pop.open) openList();
      else if (!e.altKey) {
        setActive((a) => Math.min(options.length - 1, a + 1));
        setTyped(false);
      }
      return;
    }
    if (pop.open && e.key === 'ArrowUp') {
      consume();
      if (e.altKey) pop.close();
      else {
        setActive((a) => Math.max(0, a - 1));
        setTyped(false);
      }
      return;
    }
    if (pop.open && (e.key === 'PageDown' || e.key === 'PageUp')) {
      consume();
      setActive((a) => Math.max(0, Math.min(options.length - 1, a + (e.key === 'PageDown' ? page : -page))));
      setTyped(false);
      return;
    }
    if (e.key === 'Escape' && pop.open) {
      consume();
      pop.close();
      return;
    }
    if (e.key === 'Enter') {
      if (pop.open && !typed && active >= 0) {
        consume();
        pick(active);
        return;
      }
      if (bad) {
        consume();
        shake(wrapRef.current);
        return;
      }
      if (pop.open) {
        consume();
        pop.close();
        return;
      }
    }
    if (e.key === 'Tab' && pop.open) pop.close();
    onKeyDown?.(e);
  };

  const onWrapBlur = useWholeBlur(wrapRef, listId, (e) => {
    setText(value);
    setBad(false);
    if (pop.open) pop.close();
    onBlur?.(e);
  });

  const invalid = bad || isTrue(props['aria-invalid']);
  const activeId = pop.open && active >= 0 ? `${listId}-${active}` : undefined;

  return (
    <span ref={wrapRef} className={['dt dt-time', wrapClassName].filter(Boolean).join(' ')} onBlur={onWrapBlur}>
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="text"
        inputMode="numeric"
        className={className}
        value={text}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        autoFocus={autoFocus}
        disabled={disabled}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={pop.open}
        aria-controls={pop.open ? listId : undefined}
        aria-activedescendant={activeId}
        aria-autocomplete="none"
        aria-required={required || undefined}
        aria-label={props['aria-label']}
        aria-labelledby={props['aria-labelledby']}
        aria-describedby={props['aria-describedby']}
        aria-invalid={invalid || undefined}
        data-autofocus={props['data-autofocus'] || undefined}
        onChange={(e) => onText(e.target.value)}
        onKeyDown={onKey}
        {...select}
      />
      <button
        type="button"
        className="dt-btn"
        tabIndex={-1}
        aria-label="Choose a time"
        aria-haspopup="listbox"
        aria-expanded={pop.open}
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          inputRef.current?.focus({ preventScroll: true });
          if (pop.open) pop.close();
          else openList();
        }}
      >
        <Icon name="clock" />
      </button>
      <Popover state={pop} id={listId} role="listbox" label="Times" align="left" width={124} maxWidth={160} focus={false} className="tlist-pop">
        <TimeList
          key={opened}
          listId={listId}
          options={options}
          value={value}
          active={active}
          clearable={!!clearable && value !== ''}
          onPick={pick}
          onClear={clear}
        />
      </Popover>
    </span>
  );
});

interface TimeListProps {
  listId: string;
  options: string[];
  value: string;
  active: number;
  clearable: boolean;
  onPick: (i: number) => void;
  onClear: () => void;
}

function TimeList({ listId, options, value, active, clearable, onPick, onClear }: TimeListProps) {
  const scroller = useRef<HTMLDivElement>(null);
  const centred = useRef(false);

  // Opens with the chosen time in the middle, then keeps the highlighted one in view without scrolling the page.
  useLayoutEffect(() => {
    const list = scroller.current;
    const el = active >= 0 ? document.getElementById(`${listId}-${active}`) : null;
    if (!list || !el) return;
    if (!centred.current) {
      centred.current = true;
      list.scrollTop = el.offsetTop - list.clientHeight / 2 + el.offsetHeight / 2;
      return;
    }
    if (el.offsetTop < list.scrollTop) list.scrollTop = el.offsetTop - 4;
    else if (el.offsetTop + el.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = el.offsetTop + el.offsetHeight - list.clientHeight + 4;
  }, [active, listId]);

  return (
    <>
      <div ref={scroller} className="tlist" role="presentation" onMouseDown={(e) => e.preventDefault()} onClick={(e) => e.stopPropagation()}>
        {options.map((time, i) => (
          <div
            key={time}
            id={`${listId}-${i}`}
            role="option"
            aria-selected={time === value}
            className={['tlist-opt', i === active ? 'active' : ''].filter(Boolean).join(' ')}
            onClick={() => onPick(i)}
          >
            {time}
          </div>
        ))}
      </div>
      {clearable ? (
        <div className="tlist-foot" role="presentation" onMouseDown={(e) => e.preventDefault()} onClick={(e) => e.stopPropagation()}>
          <button type="button" className="btn btn-sm btn-ghost" tabIndex={-1} onClick={onClear}>
            Clear
          </button>
        </div>
      ) : null}
    </>
  );
}

/* ---------- date and time together ---------- */

export interface DateTimeInputProps {
  /** 'YYYY-MM-DDTHH:mm', or '' until both a day and a time are filled in. */
  value: string;
  onChange: (value: string) => void;
  /** Earliest moment, 'YYYY-MM-DDTHH:mm'. The calendar uses its day. */
  min?: string;
  /** Latest moment, 'YYYY-MM-DDTHH:mm'. The calendar uses its day. */
  max?: string;
  /** Minutes between the times in the list. */
  step?: number;
  /** Id of the day box, for a label. */
  id?: string;
  /** Name of the time box for screen readers. */
  timeLabel?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  required?: boolean;
  /** Class of the wrapper around both boxes. */
  className?: string;
  /** Class of each text box: "input" unless given. */
  inputClassName?: string;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
  'data-autofocus'?: boolean;
}

/** A day box and a time box side by side for one local moment. The ref is the day box. */
export const DateTimeInput = forwardRef<HTMLInputElement, DateTimeInputProps>(function DateTimeInput(props, ref) {
  const { value, onChange, min, max, step, id, timeLabel = 'Time', autoFocus, disabled, required, className, inputClassName, onKeyDown } = props;
  const [parts, setParts] = useState(() => splitDateTime(value));
  const emitted = useRef(value);

  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    setParts(splitDateTime(value));
  }, [value]);

  const update = (next: { date: string; time: string }) => {
    setParts(next);
    const joined = joinDateTime(next.date, next.time);
    emitted.current = joined;
    if (joined !== value) onChange(joined);
  };

  const shared = {
    disabled,
    required,
    className: inputClassName,
    onKeyDown,
    'aria-describedby': props['aria-describedby'],
    'aria-invalid': props['aria-invalid'],
  };
  // With an aria-label the pair is a named group holding "Date" and "Time"; otherwise a label for the id names the day box.
  const groupLabel = props['aria-label'];
  return (
    <span className={['dt-pair', className].filter(Boolean).join(' ')} role={groupLabel ? 'group' : undefined} aria-label={groupLabel}>
      <DateInput
        ref={ref}
        id={id}
        value={parts.date}
        onChange={(date) => update({ ...parts, date })}
        min={min ? splitDateTime(min).date : undefined}
        max={max ? splitDateTime(max).date : undefined}
        autoFocus={autoFocus}
        aria-label={groupLabel ? 'Date' : undefined}
        aria-labelledby={props['aria-labelledby']}
        data-autofocus={props['data-autofocus']}
        {...shared}
      />
      <TimeInput
        value={parts.time}
        onChange={(time) => update({ ...parts, time })}
        step={step}
        aria-label={timeLabel}
        {...shared}
      />
    </span>
  );
});

/* ---------- labelled fields ---------- */

interface ShellProps {
  label: ReactNode;
  help?: ReactNode;
  error?: string | null;
  required?: boolean;
  aside?: ReactNode;
  /** Class of the field around the label and the box. */
  className?: string;
  /** Class of the text box. */
  inputClassName?: string;
}

function describedBy(id: string, error?: string | null, help?: ReactNode): string | undefined {
  if (error) return `${id}-err`;
  if (help) return `${id}-help`;
  return undefined;
}

// Help and error text under the field describe the box; an error marks it invalid. Without either, the box keeps
// any description or invalid state passed in (a message shared by two fields, say).

type DateFieldProps = ShellProps & Omit<DateInputProps, 'className'>;

/** A labelled day box with help and error text, like InputField. */
export const DateField = forwardRef<HTMLInputElement, DateFieldProps>(function DateField(
  { label, help, error, required, aside, className, inputClassName, id, 'aria-describedby': described, 'aria-invalid': invalid, ...rest },
  ref,
) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <Field label={label} help={help} error={error} required={required} aside={aside} className={className} htmlFor={fid}>
      <DateInput
        ref={ref}
        id={fid}
        className={['input', inputClassName].filter(Boolean).join(' ')}
        required={required}
        aria-invalid={!!error || isTrue(invalid)}
        aria-describedby={describedBy(fid, error, help) ?? described}
        {...rest}
      />
    </Field>
  );
});

type TimeFieldProps = ShellProps & Omit<TimeInputProps, 'className'>;

/** A labelled time box with help and error text, like InputField. */
export const TimeField = forwardRef<HTMLInputElement, TimeFieldProps>(function TimeField(
  { label, help, error, required, aside, className, inputClassName, id, 'aria-describedby': described, 'aria-invalid': invalid, ...rest },
  ref,
) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <Field label={label} help={help} error={error} required={required} aside={aside} className={className} htmlFor={fid}>
      <TimeInput
        ref={ref}
        id={fid}
        className={['input', inputClassName].filter(Boolean).join(' ')}
        required={required}
        aria-invalid={!!error || isTrue(invalid)}
        aria-describedby={describedBy(fid, error, help) ?? described}
        {...rest}
      />
    </Field>
  );
});

type DateTimeFieldProps = Omit<ShellProps, 'inputClassName'> & Omit<DateTimeInputProps, 'className'>;

/** A labelled day and time pair with help and error text. The label names the day box. */
export const DateTimeField = forwardRef<HTMLInputElement, DateTimeFieldProps>(function DateTimeField(
  { label, help, error, required, aside, className, inputClassName, id, 'aria-describedby': described, 'aria-invalid': invalid, ...rest },
  ref,
) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <Field label={label} help={help} error={error} required={required} aside={aside} className={className} htmlFor={fid}>
      <DateTimeInput
        ref={ref}
        id={fid}
        inputClassName={['input', inputClassName].filter(Boolean).join(' ')}
        required={required}
        aria-invalid={!!error || isTrue(invalid)}
        aria-describedby={describedBy(fid, error, help) ?? described}
        {...rest}
      />
    </Field>
  );
});
